import type { SelectMultipleOnSelectAll } from '@/components_deprecated'
import {
  Checkbox,
  DateRangePicker,
  MonthPicker,
  MonthWeekToggle,
  Select,
  Toggle,
} from '@/components_deprecated'
import { SelectMultipleUsers } from '@/components_deprecated/Select/SelectMultipleUsers'
import { DropdownNames } from '@/components_deprecated/Select/types'
import { UserOrderBy } from '@/modules/activities'
import { useMassActivityExportForm } from '@/modules/activities/hooks'
import { Module, ModuleOptionsValue } from '@/modules/companies'
import { responsiveExportGridAreas } from '@/modules/companies/util'
import { ExportFormat, ExportOption } from '@/modules/exports'
import {
  useExportFormatOptions,
  useExportOptions,
} from '@/modules/exports/hooks'
import { handleDateRangeChange } from '@/utils/date/handleDateRangeChange'
import { useModuleConfig } from '@/utils/hooks'
import { ComponentSwitch, Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useEffect, useMemo } from 'react'
import { isMobile } from 'react-device-detect'
import styled, { useTheme } from 'styled-components'
import type { FormButton } from '../common'
import { Field, Form, Section } from '../common'

const SelectMultipleUsersWrapper = styled.div`
  position: relative;
  min-height: 54px;
`

const NotificationWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  font-size: 1.1rem;
  line-height: 1.5rem;
`

export const ExportDriverActivitiesCard: React.FC = () => {
  const translations = useTranslate({
    exportWeekly: 'activities.export-monthly-weekly',
    exportAll: 'settings.export-all-driver-activities',
    period: 'common.period',
    exportType: 'common.type',
    exportFormat: 'common.format',
    exportMethod: 'common.export-method',
    mergeWithManualTimeEntriesActive:
      'activities.merge-with-manual-time-entries-active',

    export: 'common.export',
    orderBy: 'common.order-by',
    selectUsers: 'common.select-users',
    selectAll: 'common.select-all',
    includeAbsences: 'settings.include-absences',

    downloadOnlySupportsPdf: 'settings.download-only-supports-pdf',

    lastName: 'common.lastName',
    userType: 'common.user-type',
    externalId: 'common.external-id',
  })

  const {
    loading,
    form,
    errors,
    submit,
    updateForm,
    handleExport,
    handler,
    formValid,
    exportTypes,
  } = useMassActivityExportForm()
  const { getModuleOptionStatus: getModuleOptionStatusTimesheets } =
    useModuleConfig(Module.TIMESHEETS)
  const timesheetMergeActive = getModuleOptionStatusTimesheets(
    ModuleOptionsValue.MERGE_TIMESHEETS_WITH_ACTIVITIES
  )
  const theme = useTheme()
  const exportOptions = useExportOptions(true)
  const excludeExcel = form.exportOption === ExportOption.DOWNLOAD
  const SalaryFormats = [
    ExportFormat.POWEROFFICE,
    ExportFormat.VISMA,
    ExportFormat.VISMA_NEW,
    ExportFormat.TWENTYFOUR_SEVEN_OFFICE,
    ExportFormat.TRIPLETEX,
  ]
  const excludeFormats = excludeExcel
    ? [
        ExportFormat.EXCEL,
        ExportFormat.EXCEL_FLAT,
        ExportFormat.PDF_SALARY,
        ...SalaryFormats,
      ]
    : SalaryFormats
  const exportFormats = useExportFormatOptions(excludeFormats)

  const selectAllOnChange: SelectMultipleOnSelectAll = (
    options,
    setSelectAll
  ) => {
    return (value: boolean) => {
      if (value) {
        updateForm({ users: options.map(option => option.value) as string[] })
        setSelectAll(true)
      } else {
        updateForm({ users: [] })
        setSelectAll(false)
      }
    }
  }

  const areas = !isMobile
    ? `
        ${responsiveExportGridAreas(true)}
        'monthWeek monthWeek sort'
        'includeAbsences includeAbsences includeAbsences'
      `
    : `
        ${responsiveExportGridAreas(true)}
        'monthWeek'
        'sort'
        'includeAbsences'
      `

  const formButtons = useMemo<FormButton[]>(
    () => [
      {
        text: (
          <>
            <Icon
              icon="download"
              type="light"
              color="primary"
              margin="0 0.5ch 0 0"
            />
            {translations.export}
          </>
        ),
        type: 'submit',
        buttonProps: {
          fullWidth: isMobile,
          disabled: loading || !formValid,
        },
        onClick: submit(handleExport),
      },
    ],
    [formValid, handleExport, loading, submit, translations.export]
  )

  const orderByOptions = useMemo(
    () => [
      { value: UserOrderBy.FAMILY_NAME, label: translations.lastName },
      { value: UserOrderBy.USER_TYPES, label: translations.userType },
      { value: UserOrderBy.INTERNAL_NUMBER, label: translations.externalId },
    ],
    [translations.externalId, translations.lastName, translations.userType]
  )

  useEffect(() => {
    if (
      form.exportOption === ExportOption.DOWNLOAD &&
      form.exportFormat !== ExportFormat.PDF
    ) {
      updateForm({ exportFormat: ExportFormat.PDF })
    }
  }, [form.exportOption, form.exportFormat, updateForm, form.exportType])

  return (
    <>
      {!timesheetMergeActive && (
        <Section>
          <NotificationWrapper>
            <Icon
              icon="exclamation-triangle"
              color={theme.colors.orange}
              size="1.1rem"
              margin="2px 0 0 0"
            />
            <span>{translations.mergeWithManualTimeEntriesActive}</span>
          </NotificationWrapper>
        </Section>
      )}

      <Section
        header={translations.exportAll}
        loading={loading}
        actions={
          <Toggle
            value={form.useDateRange}
            thumbBackground="secondary"
            iconMode="svg"
            localOnIconProps={{
              icon: 'calendar-selection',
              fill: form.useDateRange ? 'white' : 'primary',
            }}
            localOffIconProps={{
              icon: 'calendar-month',
              fill: !form.useDateRange ? 'white' : 'primary',
            }}
            onChange={() => handler('useDateRange')(!form.useDateRange)}
          />
        }
      >
        <Form
          grid={{
            columns: !isMobile ? '1fr 2fr 2fr' : '1fr',
            areas: areas,
          }}
          buttons={formButtons}
        >
          <Field area="month">
            <label>{translations.period}</label>

            <ComponentSwitch>
              <ComponentSwitch.Case case={form.useDateRange}>
                <DateRangePicker
                  value={[form.dateStart, form.dateEnd]}
                  grayed={false}
                  onChange={handleDateRangeChange(updateForm)}
                />
              </ComponentSwitch.Case>

              <ComponentSwitch.Case default>
                <MonthPicker
                  value={form.month}
                  light
                  futureYears={0}
                  noYearCycle
                  onChange={handler('month')}
                />
              </ComponentSwitch.Case>
            </ComponentSwitch>
          </Field>

          <Field area="exportType">
            <label>{translations.exportType}</label>
            <Select
              value={form.exportType}
              options={exportTypes}
              fullWidth
              height="3.875rem"
              onChange={val => {
                handler('exportType')(val)
              }}
            />
          </Field>

          <Field area="exportOption">
            <label>{translations.exportMethod}</label>
            <Select
              value={form.exportOption}
              options={exportOptions}
              fullWidth
              height="3.875rem"
              onChange={handler('exportOption')}
            />
          </Field>

          <Field area="exportFormat">
            <label>{translations.exportFormat}</label>
            <Select
              value={form.exportFormat}
              options={exportFormats}
              disabled={excludeExcel}
              fullWidth
              height="3.875rem"
              onChange={handler('exportFormat')}
              error={excludeExcel ? translations.downloadOnlySupportsPdf : null}
            />
          </Field>

          <Field area="user">
            <label>{translations.selectUsers}</label>
            <SelectMultipleUsersWrapper>
              <SelectMultipleUsers
                selected={form.users ?? []}
                dropdownPageName={DropdownNames.ACTIVITIES}
                grayed={false}
                error={errors.users}
                width="100%"
                minWidth={isMobile ? '0' : '260px'}
                onChange={handler('users')}
                onSelectAll={selectAllOnChange}
                withSelectAll={true}
              />
            </SelectMultipleUsersWrapper>
          </Field>

          <Field area="monthWeek">
            <label>{translations.exportWeekly}</label>
            <MonthWeekToggle
              value={form.weekly}
              onChange={value =>
                updateForm({
                  weekly: value,
                })
              }
            />
          </Field>

          <Field area="sort">
            <label>{translations.orderBy}</label>
            <div>
              <Select
                value={form.orderBy}
                onChange={handler('orderBy')}
                options={orderByOptions}
              />
            </div>
          </Field>

          <Field area="includeAbsences">
            <label>{translations.includeAbsences}</label>
            <div>
              <Checkbox
                checked={form.includeAbsences ?? false}
                onChange={handler('includeAbsences')}
                label={translations.includeAbsences}
                inline
              />
            </div>
          </Field>
        </Form>
      </Section>
    </>
  )
}
